import { memo, type FC } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LightTooltip } from "@/components/LightTooltip";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import { LookbackSelector } from "@/components/Export/LookbackSelector";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";

export type TierSetting = "isV1Dataset" | "isPlatformPermissible" | "allTiers";

export type CompanyConfigSlice = {
  tierSetting: TierSetting;
  rgifExclusiveCompany: boolean;
  /** "Limit to V0 Companies" — sent as searchParams.isV0Dataset. */
  isV0Dataset: boolean;
  /**
   * Tech-install recency that qualifies a company (months or "ALLTIME"),
   * sent as the Universe JSON's searchParams.installAge. OppGen keeps it in
   * the Tech Installs filter group (`exportCompaniesInstallLookback`).
   */
  installAge: number | string;
};

// OppGen's defaults (OpportunityGeneratorContext / TiersAndRgifSettings).
const defaultSlice: CompanyConfigSlice = {
  tierSetting: "isPlatformPermissible",
  rgifExclusiveCompany: true,
  isV0Dataset: false,
  installAge: 24,
};

const isDefaultSlice = (slice: CompanyConfigSlice) =>
  slice.tierSetting === defaultSlice.tierSetting &&
  slice.rgifExclusiveCompany === defaultSlice.rgifExclusiveCompany &&
  slice.isV0Dataset === defaultSlice.isV0Dataset &&
  slice.installAge === defaultSlice.installAge;

/**
 * The search params OppGen derives from its Company Configuration — always
 * sent on export, at their defaults when the user never opened the panel.
 */
export const getCompanyConfigSearchParams = (
  companyConfigUi: CompanyFilters["companyConfigUi"] | undefined
) => {
  const tierSetting = companyConfigUi?.tierSetting ?? defaultSlice.tierSetting;
  return {
    ...deriveTierFlags(tierSetting),
    isV0Dataset: companyConfigUi?.isV0Dataset ?? defaultSlice.isV0Dataset,
    rgifExclusiveCompany:
      companyConfigUi?.rgifExclusiveCompany ??
      defaultSlice.rgifExclusiveCompany,
  };
};

export const getCompanyConfigInstallAge = (
  companyConfigUi: CompanyFilters["companyConfigUi"] | undefined
): number | string => companyConfigUi?.installAge ?? defaultSlice.installAge;

// Derives the isPlatformPermissible/isV1Dataset booleans OppGen's search API
// and export payload expect from the single tierSetting radio value.
export const deriveTierFlags = (
  tierSetting: TierSetting
): { isPlatformPermissible: boolean; isV1Dataset: boolean } => ({
  isPlatformPermissible: tierSetting === "isPlatformPermissible",
  isV1Dataset: tierSetting === "isV1Dataset",
});

const CompanyConfigFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("companyConfig");
  const { setSlice } = useCompanyFilterActions();

  const update = (patch: Partial<CompanyConfigSlice>) =>
    setSlice("companyConfig", { ...slice, ...patch });

  return (
    <Box sx={{ p: 1 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontStyle: "italic" }}
      >
        This configuration must be set before matching an uploaded account list.
        If these settings are changed after matching, rematching is required.
      </Typography>
      <Typography variant="body2" sx={{ mt: 2, fontWeight: 500 }}>
        Tier Selection
      </Typography>
      <RadioGroup
        value={slice.tierSetting}
        onChange={(_, v) => update({ tierSetting: v as TierSetting })}
      >
        <FormControlLabel
          value="isV1Dataset"
          control={
            <Radio size="small" data-testid="company-config-tier1-radio" />
          }
          label={<Typography variant="body2">Tier 1</Typography>}
        />
        <FormControlLabel
          value="isPlatformPermissible"
          control={
            <Radio size="small" data-testid="company-config-tier1-3-radio" />
          }
          label={<Typography variant="body2">Tier 1 + Tier 3</Typography>}
        />
        <FormControlLabel
          value="allTiers"
          control={
            <Radio size="small" data-testid="company-config-tier1-3-4-radio" />
          }
          label={
            <Typography variant="body2">Tier 1 + Tier 3 + Tier 4</Typography>
          }
        />
      </RadioGroup>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        RGIF Companies
      </Typography>
      <RadioGroup
        value={slice.rgifExclusiveCompany ? "yes" : "no"}
        onChange={(_, v) => update({ rgifExclusiveCompany: v === "yes" })}
      >
        <FormControlLabel
          value="yes"
          control={
            <Radio size="small" data-testid="company-config-rgif-yes-radio" />
          }
          label={<Typography variant="body2">Yes</Typography>}
        />
        <FormControlLabel
          value="no"
          control={
            <Radio size="small" data-testid="company-config-rgif-no-radio" />
          }
          label={<Typography variant="body2">No</Typography>}
        />
      </RadioGroup>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Limit to V0 Companies
      </Typography>
      <RadioGroup
        value={slice.isV0Dataset ? "yes" : "no"}
        onChange={(_, v) => update({ isV0Dataset: v === "yes" })}
      >
        <FormControlLabel
          value="yes"
          control={
            <Radio size="small" data-testid="company-config-v0-yes-radio" />
          }
          label={<Typography variant="body2">Yes</Typography>}
        />
        <FormControlLabel
          value="no"
          control={
            <Radio size="small" data-testid="company-config-v0-no-radio" />
          }
          label={<Typography variant="body2">No</Typography>}
        />
      </RadioGroup>
    </Box>
  );
});

CompanyConfigFilter.displayName = "CompanyConfigFilter";

/**
 * OppGen's "Install Last Verified Date Range [Universe Only]", which it shows
 * in the Tech Installs settings next to Install Localization rather than in
 * Company Configuration. Stored on the companyConfig slice (installAge).
 */
export const InstallAgeSetting: FC = memo(() => {
  const universeJsonExportEnabled = useFeatureFlag("universeJsonExport", false);
  const slice = useCompanyFilterSlice("companyConfig");
  const { setSlice } = useCompanyFilterActions();

  if (!universeJsonExportEnabled) return null;

  const helpText = (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
        Install Last Verified Date Range [Universe Only]
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", lineHeight: 1.5 }}
      >
        The default last date verified in Platform is 2 years. Configure here to
        expand the date look back range, including choosing all time detections.
        The company list will not show all companies returned for an expanded
        date range query.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", mb: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Install Last Verified [Universe Only]
        </Typography>
        <LightTooltip title={helpText} placement="top">
          <IconButton
            size="small"
            aria-label="Install last verified date range info"
            sx={{ color: "text.secondary", opacity: 0.6 }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </LightTooltip>
      </Stack>
      <LookbackSelector
        value={slice.installAge}
        onChange={(installAge) =>
          setSlice("companyConfig", { ...slice, installAge })
        }
        label="Last Verified Date"
      />
    </Box>
  );
});

InstallAgeSetting.displayName = "InstallAgeSetting";

export const companyConfigModule: FilterModule<CompanyConfigSlice> = {
  key: "companyConfig",
  defaultSlice,
  Component: CompanyConfigFilter,
  // Emit nothing at the default so an untouched module leaves
  // `appliedFilters` undefined (the "no filters" state the page relies on).
  toCompanyFilters: (slice: CompanyConfigSlice): Partial<CompanyFilters> =>
    isDefaultSlice(slice)
      ? {}
      : {
          companyConfigUi: {
            tierSetting: slice.tierSetting,
            rgifExclusiveCompany: slice.rgifExclusiveCompany,
            isV0Dataset: slice.isV0Dataset,
            installAge: slice.installAge,
          },
        },
  fromCompanyFilters: (filters: CompanyFilters): CompanyConfigSlice => ({
    tierSetting:
      filters.companyConfigUi?.tierSetting ?? defaultSlice.tierSetting,
    rgifExclusiveCompany:
      filters.companyConfigUi?.rgifExclusiveCompany ??
      defaultSlice.rgifExclusiveCompany,
    isV0Dataset:
      filters.companyConfigUi?.isV0Dataset ?? defaultSlice.isV0Dataset,
    installAge: filters.companyConfigUi?.installAge ?? defaultSlice.installAge,
  }),
  getChips: () => [],
  getCount: (slice) => (isDefaultSlice(slice) ? 0 : 1),
};
